/**
 * @fileoverview Organization switcher dropdown component.
 * Displayed in the app header, allows users to switch between organizations.
 * Super admins see all organizations plus an "All Organizations" option.
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { setActiveOrgId } from '../services/api';

/**
 * OrgSwitcher renders a dropdown showing the current organization
 * and allowing the user to switch to another org they belong to.
 * Super admins see all orgs in the system plus "All Organizations".
 *
 * @returns {JSX.Element|null} The org switcher dropdown, or null if non-admin with <= 1 org
 */
function OrgSwitcher() {
  const { activeOrg, orgs, switchOrg, impersonatedOrg, isSuperAdmin, adminApiPrefix } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [allOrgs, setAllOrgs] = useState([]);
  const location = useLocation();

  // Super admins: fetch all organizations in the system
  useEffect(() => {
    if (isSuperAdmin && adminApiPrefix) {
      api.adminListOrgs(adminApiPrefix)
        .then(({ data }) => {
          const orgList = data.organizations || data || [];
          setAllOrgs(orgList);
        })
        .catch(err => console.error('Failed to fetch all orgs:', err));
    }
  }, [isSuperAdmin, adminApiPrefix]);

  // Determine which org list to display
  const displayOrgs = isSuperAdmin && allOrgs.length > 0 ? allOrgs : orgs;

  // Don't render if non-admin user has 0 or 1 org
  if (!isSuperAdmin && (!orgs || orgs.length <= 1)) {
    return null;
  }

  // Hide during impersonation — use banner to exit instead
  if (impersonatedOrg) {
    return null;
  }

  /**
   * Handle organization change from the dropdown.
   * "All Organizations" (value="") clears the org filter for super admins.
   * Otherwise navigates to the new org's slug URL.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event
   */
  const handleChange = async (e) => {
    const orgId = e.target.value;

    // "All Organizations" selected — clear org scope
    if (orgId === '') {
      setSwitching(true);
      setActiveOrgId(null);
      window.location.href = '/org/all/dashboard';
      return;
    }

    const newOrg = displayOrgs.find(o => String(o.id) === String(orgId));
    if (!newOrg || String(orgId) === String(activeOrg?.id)) return;

    setSwitching(true);
    try {
      await switchOrg(orgId);
      const currentSubPath = location.pathname.replace(/^\/org\/[^/]+/, '') || '/dashboard';
      window.location.href = `/org/${newOrg.slug}${currentSubPath}`;
    } catch {
      // switchOrg already logs the error
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="org-switcher">
      <label htmlFor="org-switcher-select" className="business-selector__label">
        Org:
      </label>
      <select
        id="org-switcher-select"
        value={activeOrg?.id || ''}
        onChange={handleChange}
        disabled={switching}
        aria-label="Switch organization"
      >
        {isSuperAdmin && (
          <option value="">All Organizations</option>
        )}
        {displayOrgs.map(org => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export default OrgSwitcher;
